// External
import React from 'react';

// Internal
import {
  AccountBenefitsContentContainer,
  ExpandedValuePropsContainer,
  ExpandedValuePropsRowThreeIcons,
  ExpandedValuePropsRowV2,
  ExpandedValuePropsRowV3,
  ExpandedValuePropsText,
  RewardsText,
  StretchRewardsContentContainer,
  StretchRewardsHeader,
  StretchRewardsOfferDisclaimer,
} from '@/components/CheckoutIncentives/StyledComponents';
import { features, getContent, getLocale, useMountLogging } from '@/lib';
import { useStoreState } from '@/store';
import { CloseSmIcon } from '@/components/Common';
import CheckMark from '../Common/Spots/Icons/CheckMark';
import { PRIMARY_COLOR_V2 } from '@/styles';

type AccountBenefitParams = {
  toggleComponent?: JSX.Element; // Optional, as we use this component for forced signup as well.
};

const AccountBenefits = ({
  toggleComponent,
}: AccountBenefitParams): JSX.Element => {
  useMountLogging('AccountBenefits');
  const { country } = getLocale();
  const isOptionalSignup = features.isActive('isOptionalSignup');
  const isForcedSignup = features.isActive('isForcedSignUp');
  const onboardValue = useStoreState(({ form }) => form.onboardOption?.value);

  const accountBenefitsContentKey = isOptionalSignup
    ? 'checkoutIncentives.stretchRewards.v2.optional.description'
    : 'checkoutIncentives.stretchRewards.v2.forced.signup.description';

  const isSignupToggle = Boolean(onboardValue === 'signup');

  const forcedSignup = isSignupToggle || isForcedSignup;

  return (
    <ExpandedValuePropsContainer data-testid="account-benefits">
      <StretchRewardsHeader>
        <StretchRewardsContentContainer>
          <RewardsText data-testid="combo-rewards-text">
            {getContent(accountBenefitsContentKey)}
          </RewardsText>
        </StretchRewardsContentContainer>
        {isOptionalSignup && toggleComponent}
      </StretchRewardsHeader>
      <ExpandedValuePropsRowV2>
        <AccountBenefitsContentContainer data-testid="combo-rewards">
          <ExpandedValuePropsRowV3>
            <ExpandedValuePropsRowThreeIcons>
              {forcedSignup ? (
                <CheckMark color={PRIMARY_COLOR_V2} />
              ) : (
                <CloseSmIcon />
              )}
              <ExpandedValuePropsText>
                {getContent(
                  forcedSignup
                    ? 'checkoutIncentives.stretchRewards.v2.unlimitedCashBack'
                    : 'checkoutIncentives.stretchRewards.v2.not.unlimitedCashBack',
                )}
              </ExpandedValuePropsText>
            </ExpandedValuePropsRowThreeIcons>
            <ExpandedValuePropsRowThreeIcons>
              {forcedSignup ? (
                <CheckMark color={PRIMARY_COLOR_V2} />
              ) : (
                <CloseSmIcon />
              )}
              <ExpandedValuePropsText>
                {getContent(
                  forcedSignup
                    ? 'checkoutIncentives.stretchRewards.v2.eligiblePurchaseProtection'
                    : 'checkoutIncentives.stretchRewards.v2.not.eligiblePurchaseProtection',
                )}
              </ExpandedValuePropsText>
            </ExpandedValuePropsRowThreeIcons>
            <StretchRewardsOfferDisclaimer>
              {getContent(
                'checkoutIncentives.stretchRewards.v2.offerDisclaimer',
                { country },
                { useHTML: true },
              )}
            </StretchRewardsOfferDisclaimer>
          </ExpandedValuePropsRowV3>
        </AccountBenefitsContentContainer>
      </ExpandedValuePropsRowV2>
    </ExpandedValuePropsContainer>
  );
};

export { AccountBenefits };
